import React from 'react';
import { Helmet } from 'react-helmet-async';

// Assuming your Property type looks something like this based on usage:
interface Property {
  name: string;
  description: string; // You're using a hardcoded description for schema, but keeping this for completeness
  maxOccupancy?: number;
  numberOfBedrooms?: number; // This should be 5
  numberOfBathrooms?: number; // This should reflect total *full* bathrooms
  numberOfBeds?: number;
  amenities: string[]; // e.g., ['pool', 'wifi', 'kitchen']
  images: string[]; // Relative paths, e.g., ['/images/pool/main.jpg']
}

interface StructuredDataProps {
  property: Property;
}

const StructuredData: React.FC<StructuredDataProps> = ({ property }) => {

  // --- Common Reusable Data Blocks ---

  // Property address details
  const address = {
    '@type': 'PostalAddress',
    'streetAddress': 'Calle Cañete la Real 39',
    'addressLocality': 'Pinos de Alhaurín',
    'addressRegion': 'Málaga',
    'postalCode': '29130',
    'addressCountry': 'ES'
  };

  // Property coordinates
  const geoCoordinates = {
    '@type': 'GeoCoordinates',
    'latitude': '36.6849',
    'longitude': '-4.5699'
  };

  // Convert amenity keys to human-readable names for schema
  // We use specific, descriptive names to target long-tail search queries
  const amenityFeatures = property.amenities.map(amenityKey => {
    const amenityName = amenityKey.split('.').pop() || amenityKey;
    const amenityMap: { [key: string]: string } = {
      'pool': 'Private Pool (2.3m deep)',
      'wifi': '1 Gbps Fiber Wi-Fi',
      'kitchen': "Gourmet Chef's Kitchen",
      'parking': 'Private Garage & Parking',
      'ac': 'Air Conditioning (All Rooms)',
      'tv': 'Smart TV & PlayStation 4',
      'dining': 'Al-Fresco Dining for 16',
      'bbq': 'Gas BBQ & Outdoor Kitchen',
      'charger': 'EV Charger',
      'lighting': 'Smart Garden Lighting',
      'washing': 'Laundry Room',
      'smoking': 'Smoking Allowed (Outdoors)',
      'pets': 'Pets Allowed (On Request)'
    };

    const feature: { [key: string]: any } = {
      '@type': 'LocationFeatureSpecification',
      'name': amenityMap[amenityName] || amenityName.charAt(0).toUpperCase() + amenityName.slice(1).replace(/([A-Z])/g, ' $1').trim(),
      'value': true
    };

    return feature;
  });

  // Full image URLs for schema
  const imageUrls = property.images.map(img =>
    `https://villapinosalhaurin.com${img}`
  );

  // Add interior images to complement pool images
  const extendedImageUrls = [
    ...imageUrls,
    'https://villapinosalhaurin.com/images/interior/master-bedroom.jpg',
    'https://villapinosalhaurin.com/images/interior/bathroom-guestroom45.jpg',
    'https://villapinosalhaurin.com/images/interior/living-room.jpg',
    'https://villapinosalhaurin.com/images/interior/kitchen.jpg',
    'https://villapinosalhaurin.com/images/interior/guest-bedroom-1.jpg',
    'https://villapinosalhaurin.com/images/interior/guest-bedroom-2.jpg',
    'https://villapinosalhaurin.com/images/interior/bathroom-private.jpg',
    'https://villapinosalhaurin.com/images/interior/utility-room.jpg'
  ];

  // --- VacationRental Schema ---
  // NOTE: Only including data that actually exists on the website
  // No fake reviews or ratings since reviews array is empty

  const vacationRentalSchema = {
    '@context': 'https://schema.org',
    '@type': 'VacationRental',
    'additionalType': 'Villa',
    'identifier': 'villa-pinos-alhaurin-29130',
    'name': property.name,
    'description': 'Private 5-bedroom family villa with pool in Pinos de Alhaurín (bordering Alhaurín el Grande). Luxury accommodation for Costa del Sol vacations. Direct owner booking (no fees). 15 min from Málaga Airport.',
    'address': address,
    'geo': geoCoordinates,
    'containsPlace': {
      '@type': 'Accommodation',
      'additionalType': 'EntirePlace',
      'bed': [
        { '@type': 'BedDetails', 'numberOfBeds': 1, 'typeOfBed': 'King' },
        { '@type': 'BedDetails', 'numberOfBeds': 4, 'typeOfBed': 'Twin' },
        { '@type': 'BedDetails', 'numberOfBeds': 2, 'typeOfBed': 'Double' },
        { '@type': 'BedDetails', 'numberOfBeds': 2, 'typeOfBed': 'Twin' },
        { '@type': 'BedDetails', 'numberOfBeds': 1, 'typeOfBed': 'Double' }
      ],
      'occupancy': {
        '@type': 'QuantitativeValue',
        'value': property.maxOccupancy,
        'unitText': 'PERSONS'
      },
      'amenityFeature': amenityFeatures,
      'floorSize': {
        '@type': 'QuantitativeValue',
        'value': 400,
        'unitCode': 'MTK'
      },
      'numberOfBathroomsTotal': 4.5,
      'numberOfBedrooms': property.numberOfBedrooms,
      'numberOfRooms': 14
    },
    'image': extendedImageUrls,
    'checkinTime': '14:00:00+01:00',
    'checkoutTime': '11:00:00+01:00',
    'priceRange': '€€€',
    'priceCurrency': 'EUR',
    'offers': {
      '@type': 'Offer',
      'priceCurrency': 'EUR',
      'availability': 'https://schema.org/InStock',
      'description': 'Direct booking rate - 0% Commission / No Booking Fees'
    },
    'url': 'https://villapinosalhaurin.com',
    'telephone': '+32 475 490 137',
    'paymentAccepted': 'Bank Transfer',
    'cancellationPolicy': 'Moderate',
    'openingHours': 'Mo,Tu,We,Th,Fr,Sa,Su 14:00-20:00'
  };

  // --- LodgingBusiness Schema ---
  const lodgingBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LodgingBusiness',
    'name': property.name,
    'image': 'https://villapinosalhaurin.com/images/pool/main.jpg',
    'address': address,
    'geo': geoCoordinates,
    'url': 'https://villapinosalhaurin.com',
    'priceRange': '€€€',
    'priceCurrency': 'EUR',
    'amenityFeature': amenityFeatures,
    'checkinTime': '14:00-20:00',
    'checkoutTime': '11:00',
    'paymentAccepted': 'Bank Transfer',
    'cancellationPolicy': 'Moderate',
    'telephone': '+32 475 490 137',
    'openingHours': 'Mo,Tu,We,Th,Fr,Sa,Su 14:00-20:00'
  };

  // --- ImageObject Schemas ---
  const imageObjectSchemas = extendedImageUrls.map((img, index) => {
    const descriptions = [
      'Private pool main view',
      'Pool at dawn',
      'Pool at night',
      'Pool and garden',
      'Pool evening view',
      'Master bedroom interior',
      'Main shared bathroom',
      'Living room and kitchen area',
      'Kitchen area',
      'Guest bedroom 1',
      'Guest bedroom 2',
      'Private en-suite bathroom',
      'Utility and washing room'
    ];

    const names = [
      'Main Pool View',
      'Pool at Dawn',
      'Pool at Night',
      'Pool and Garden',
      'Pool Evening',
      'Master Bedroom',
      'Main Shared Bathroom',
      'Living Room',
      'Kitchen',
      'Guest Room 1',
      'Guest Room 2',
      'Private Bathroom',
      'Utility Room'
    ];

    return {
      '@context': 'https://schema.org',
      '@type': 'ImageObject',
      'url': img,
      'description': `Villa Pinos Alhaurín - ${descriptions[index] || 'Property image'}`,
      'name': `Villa Pinos Alhaurín - ${names[index] || `Image ${index + 1}`}`,
      'caption': `Villa Pinos Alhaurín - ${names[index] || 'Property photo'}`,
      'representativeOfPage': index === 0
    };
  });

  // Separate PostalAddress schema
  const postalAddressSchema = {
    '@context': 'https://schema.org',
    '@type': 'PostalAddress',
    'streetAddress': 'Calle Cañete la Real 39',
    'addressLocality': 'Pinos de Alhaurín',
    'addressRegion': 'Málaga',
    'postalCode': '29130',
    'addressCountry': 'ES'
  };

  // Separate GeoCoordinates schema
  const geoCoordinatesSchema = {
    '@context': 'https://schema.org',
    '@type': 'GeoCoordinates',
    'latitude': '36.6849',
    'longitude': '-4.5699'
  };

  // Organization schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': property.name,
    'url': 'https://villapinosalhaurin.com',
    'logo': 'https://villapinosalhaurin.com/images/logo/villa_pinos_alhaurin_logo.png'
  };

  // LocalBusiness schema
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': property.name,
    'image': 'https://villapinosalhaurin.com/images/pool/main.jpg',
    'address': address,
    'geo': geoCoordinates,
    'url': 'https://villapinosalhaurin.com',
    'telephone': '+32 475 490 137',
    'priceRange': '€€€',
    'openingHours': 'Mo,Tu,We,Th,Fr,Sa,Su 14:00-20:00'
  };

  // Combined schema array
  const allSchemas = [
    vacationRentalSchema,
    lodgingBusinessSchema,
    ...imageObjectSchemas,
    postalAddressSchema,
    geoCoordinatesSchema,
    organizationSchema,
    localBusinessSchema
  ];

  return (
    <Helmet>
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(allSchemas, null, 2)}
      </script>
    </Helmet>
  );
};

export default StructuredData;
